import React, { useEffect, useRef } from 'react';
import { Alert } from 'reactstrap';
import { TypedMemo } from '../../../common/types';
import { StudioActionFeedback } from './studioActionErrorText';

interface StudioActionErrorProps {
  feedback: StudioActionFeedback | null;
}

/**
 * The result of a failed Studio action, next to the button that started it ("Make it", Generate,
 * Try again, Make another). It stays until the user changes a field or tries again. When it shows,
 * it scrolls into view, so the user sees it also when the button is low on the page.
 */
const StudioActionErrorComponent: React.FC<StudioActionErrorProps> = ({ feedback }) => {
  const ref = useRef<HTMLDivElement>(null);
  const message = feedback?.message;

  useEffect(() => {
    const node = ref.current;
    if (message === undefined || node === null || typeof node.scrollIntoView !== 'function') {
      return;
    }
    node.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [message]);

  if (feedback === null) {
    return null;
  }
  return (
    <div ref={ref}>
      <Alert
        color={feedback.tone}
        className="mb-0 small text-break"
        role={feedback.tone === 'danger' ? 'alert' : 'status'}
        data-testid="studio-action-error"
      >
        {feedback.message}
      </Alert>
    </div>
  );
};

export const StudioActionError = TypedMemo(StudioActionErrorComponent);
